import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import SendIcon from "@mui/icons-material/Send";
import { useHistory } from "react-router-dom";

import PageHeader from "../../app/layout/PageHeader";
import { api } from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import { useAccess } from "../../context/AccessContext";
import { FEATURE_CODES } from "../../constants/constants";
import { ServiceDto } from "../types/types";
import type { DuplicatePartyGroup } from "../features/manage-party/DuplicatePartiesPage";
import "../../css/buttons.css";
import "../../css/cards.css";
import "../../css/tables.css";
import "./DashboardPage.css";

interface ServiceInstanceDto {
  serviceInstanceId: number;
  serviceId: number;
  serviceName: string;
  serviceColorHex: string;
  startDateTime: string;
  endDateTime: string;
  status: string;
  capacity?: number | null;
}

interface UpcomingServiceRow {
  id: number;
  serviceId: number;
  service: string;
  serviceColorHex?: string;
  nextInstance: string;
  serviceInstanceId?: number;
  startDateTime?: string;
  endDateTime?: string;
}

interface ServiceSnapshot {
  members: number | null;
  registrations: number | null;
  rotaAssigned: number | null;
}

interface ComplianceItemDto {
  partyId: number;
  displayName: string;
  partyType: string;
  reason: string;
}

interface ComplianceContactDto {
  partyId: number;
  displayName: string;
  partyType: string;
  missingEmail: boolean;
  missingMobile: boolean;
  emailVerified: boolean;
  mobileVerified: boolean;
  reason: string;
}

interface DashboardComplianceDto {
  missingServiceRegistrations: ComplianceItemDto[];
  contactIssues: ComplianceContactDto[];
}

interface CommunityUpdateDto {
  communityUpdateId: number;
  organisationPartyId: number;
  organisationName: string;
  updateType: "News" | "Request" | "Update" | "Giveaway";
  title: string;
  body: string;
  publishedDate: string;
  expiresDate?: string | null;
}

const toDateInputValue = (date: Date) => date.toISOString().substring(0, 10);

const formatInstanceWindow = (startValue: string, endValue: string) => {
  const start = new Date(startValue);
  const end = new Date(endValue);

  if (Number.isNaN(start.getTime())) {
    return "-";
  }

  const date = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  }).format(start);

  const startTime = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(start);

  if (Number.isNaN(end.getTime())) {
    return `${date}, ${startTime}`;
  }

  const endTime = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(end);

  return `${date}, ${startTime} - ${endTime}`;
};

interface ServiceManagementCardProps {
  selectedServiceId: number | null;
  onSelectedServiceChange: React.Dispatch<React.SetStateAction<number | null>>;
}

function ServiceManagementCard({
  selectedServiceId,
  onSelectedServiceChange,
}: ServiceManagementCardProps) {
  const history = useHistory();
  const { selectedTenant, isServiceManager, isTenantAdmin } = useAuth();
  const { hasFeature } = useAccess();
  const tenantId = selectedTenant?.tenantId;
  const [rows, setRows] = useState<UpcomingServiceRow[]>([]);
  const [availableInstances, setAvailableInstances] = useState<
    ServiceInstanceDto[]
  >([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [snapshot, setSnapshot] = useState<ServiceSnapshot>({
    members: null,
    registrations: null,
    rotaAssigned: null,
  });
  const [pendingRegistrationCount, setPendingRegistrationCount] = useState<
    number | null
  >(null);
  const canViewMembers = hasFeature(FEATURE_CODES.SERVICE_MEMBERS);
  const canViewRota = hasFeature(FEATURE_CODES.SERVICE_ROTA);
  const canViewSessions = hasFeature(FEATURE_CODES.SERVICE_SCHEDULE_SESSIONS);

  useEffect(() => {
    if (!tenantId) {
      setRows([]);
      setAvailableInstances([]);
      onSelectedServiceChange(null);
      return;
    }

    let isMounted = true;

    const loadUpcomingServices = async () => {
      setIsLoading(true);
      setLoadError(false);

      try {
        const services = await api<ServiceDto[]>(
          `/api/services?tenantId=${tenantId}`,
          {
            suppressLoading: true,
            notifyError: false,
          },
        );

        if (!services.length) {
          if (isMounted) {
            setRows([]);
            setAvailableInstances([]);
            onSelectedServiceChange(null);
          }
          return;
        }

        const from = new Date();
        const to = new Date();
        to.setDate(to.getDate() + 365);

        const instances = await api<ServiceInstanceDto[]>(
          "/api/ServicesCalendar/search",
          {
            method: "POST",
            body: {
              serviceIds: services.map((service) => service.serviceId),
              fromDate: toDateInputValue(from),
              toDate: toDateInputValue(to),
            },
            suppressLoading: true,
            notifyError: false,
          },
        );

        const now = Date.now();
        const activeInstances = instances
          .filter(
            (instance) =>
              instance.status !== "Cancelled" &&
              new Date(instance.endDateTime).getTime() >= now,
          )
          .sort(
            (a, b) =>
              new Date(a.startDateTime).getTime() -
              new Date(b.startDateTime).getTime(),
          );
        const nextInstanceByServiceId = new Map<number, ServiceInstanceDto>();

        activeInstances.forEach((instance) => {
          if (!nextInstanceByServiceId.has(instance.serviceId)) {
            nextInstanceByServiceId.set(instance.serviceId, instance);
          }
        });

        const nextRows = services.map((service) => {
          const instance = nextInstanceByServiceId.get(service.serviceId);

          return {
            id: service.serviceId,
            serviceId: service.serviceId,
            service: service.name,
            serviceColorHex: service.colorHex,
            nextInstance: instance
              ? formatInstanceWindow(
                  instance.startDateTime,
                  instance.endDateTime,
                )
              : "No upcoming instance",
            serviceInstanceId: instance?.serviceInstanceId,
            startDateTime: instance?.startDateTime,
            endDateTime: instance?.endDateTime,
          };
        });

        if (isMounted) {
          setRows(nextRows);
          setAvailableInstances(activeInstances);
          onSelectedServiceChange((current) =>
            current && nextRows.some((row) => row.serviceId === current)
              ? current
              : (nextRows[0]?.serviceId ?? null),
          );
        }
      } catch {
        if (isMounted) {
          setRows([]);
          setAvailableInstances([]);
          setLoadError(true);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadUpcomingServices();

    return () => {
      isMounted = false;
    };
  }, [tenantId, onSelectedServiceChange]);

  const registerSession = (row: UpcomingServiceRow) => {
    if (!row.serviceInstanceId || !row.startDateTime || !row.endDateTime) {
      return;
    }

    history.push("/party-service-registration", {
      registrationOrigin: "dashboard",
      session: {
        serviceId: row.serviceId,
        serviceName: row.service,
        serviceInstanceId: row.serviceInstanceId,
        startDateTime: row.startDateTime,
        endDateTime: row.endDateTime,
      },
    });
  };

  const selectedService =
    rows.find((row) => row.serviceId === selectedServiceId) ?? rows[0];
  const selectedServiceInstances = selectedService
    ? availableInstances.filter(
        (instance) => instance.serviceId === selectedService.serviceId,
      )
    : [];
  const now = Date.now();
  const liveInstance = selectedServiceInstances.find(
    (instance) =>
      new Date(instance.startDateTime).getTime() <= now &&
      new Date(instance.endDateTime).getTime() > now,
  );
  const selectedInstance =
    liveInstance ??
    selectedServiceInstances.find(
      (instance) => instance.serviceInstanceId === selectedInstanceId,
    ) ??
    selectedServiceInstances[0];
  const selectedServiceSession: UpcomingServiceRow | undefined = selectedService
    ? {
        ...selectedService,
        nextInstance: selectedInstance
          ? formatInstanceWindow(
              selectedInstance.startDateTime,
              selectedInstance.endDateTime,
            )
          : "No upcoming instance",
        serviceInstanceId: selectedInstance?.serviceInstanceId,
        startDateTime: selectedInstance?.startDateTime,
        endDateTime: selectedInstance?.endDateTime,
      }
    : undefined;

  useEffect(() => {
    const serviceId = selectedService?.serviceId;
    const instanceId = selectedInstance?.serviceInstanceId;
    setSnapshot({ members: null, registrations: null, rotaAssigned: null });
    setPendingRegistrationCount(null);
    if (!serviceId) return;

    let cancelled = false;
    const quietRequest = { suppressLoading: true, notifyError: false };
    if (canViewMembers) {
      api<unknown[]>(`/api/services/${serviceId}/members`, quietRequest)
        .then((members) => {
          if (!cancelled) setSnapshot((current) => ({ ...current, members: members.length }));
        })
        .catch(() => {});
    }
    if (instanceId && canViewSessions) {
      api<number>(`/api/serviceinstances/${instanceId}/registrations/count`, quietRequest)
        .then((registrations) => {
          if (!cancelled) setSnapshot((current) => ({ ...current, registrations }));
        })
        .catch(() => {});
    }
    if (instanceId && canViewRota && canViewSessions) {
      api<unknown[]>(`/api/service-instances/${instanceId}/rota`, quietRequest)
        .then((assignments) => {
          if (!cancelled) setSnapshot((current) => ({ ...current, rotaAssigned: assignments.length }));
        })
        .catch(() => {});
    }
    if (
      isServiceManager &&
      hasFeature(FEATURE_CODES.SERVICE_REGISTRATION)
    ) {
      api<number>(
        `/api/admin/public-registrations/count?serviceId=${serviceId}`,
        quietRequest,
      )
        .then((count) => {
          if (!cancelled) setPendingRegistrationCount(count);
        })
        .catch(() => {});
    }

    return () => { cancelled = true; };
  }, [selectedService?.serviceId, selectedInstance?.serviceInstanceId, canViewMembers, canViewRota, canViewSessions, isServiceManager, hasFeature]);

  const glanceItems = [
    { label: "Members", value: snapshot.members },
    { label: "Registered", value: snapshot.registrations },
    { label: "Rota assigned", value: snapshot.rotaAssigned },
    { label: "Scheduled sessions", value: selectedServiceInstances.length },
  ];

  return (
    <section className="card dashboard-panel dashboard-service-management">
      <div className="dashboard-service-management-header">
        <h2 className="dashboard-panel-title">Select service</h2>
        {!isLoading && selectedService && (
          <div
            className="dashboard-service-management-controls"
            style={{
              ["--service-color" as string]:
                selectedService.serviceColorHex || "#f59e0b",
            }}
          >
            <label className="dashboard-service-selector dashboard-service-selector--header">
              <span className="sr-only">Select a service</span>
              <span className="dashboard-service-selector-dot" style={{
                backgroundColor: selectedService.serviceColorHex || "#f59e0b",
              }} />
              <select value={selectedService.serviceId} onChange={(event) => {
                setSelectedInstanceId(null);
                onSelectedServiceChange(Number(event.target.value));
              }}>
                {rows.map((row) => <option key={row.id} value={row.serviceId}>{row.service}</option>)}
              </select>
              <UnfoldMoreIcon fontSize="small" aria-hidden="true" />
            </label>
            <button type="button" className="dashboard-all-services-link"
              onClick={() => history.push(`/admin/manage-services?serviceId=${selectedService.serviceId}`)}
              aria-label={`Open ${selectedService.service}`} title={`Open ${selectedService.service}`}>
              <ChevronRightIcon />
            </button>
          </div>
        )}
      </div>
      {isLoading && <p className="muted">Loading next sessions...</p>}
      {!isLoading && selectedService && (
        <div className="dashboard-service-workflow">
          <div className="dashboard-service-section dashboard-service-section--next-session">
            <h3 className="dashboard-service-section-title">Next session</h3>
            <div className="dashboard-service-session-row">
              <span className="dashboard-service-session-icon">
                <CalendarMonthIcon fontSize="small" aria-hidden="true" />
              </span>
              <span>{selectedServiceSession?.nextInstance}</span>
              {selectedServiceInstances.length > 1 && (
                <label className="dashboard-inline-session-picker" title="Select a different session">
                  <span className="sr-only">Select a {selectedService.service} session</span>
                  <ExpandMoreIcon fontSize="small" aria-hidden="true" />
                  <select aria-label={`Select a ${selectedService.service} session`}
                    value={selectedInstance?.serviceInstanceId ?? ""}
                    onChange={(event) => setSelectedInstanceId(Number(event.target.value))}>
                    {selectedServiceInstances.map((instance) => (
                      <option key={instance.serviceInstanceId} value={instance.serviceInstanceId}>
                        {formatInstanceWindow(instance.startDateTime, instance.endDateTime)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>

          <div className="dashboard-service-section">
            <h3 className="dashboard-service-section-title">Quick actions</h3>
            <div
              className={`dashboard-service-action-grid${
                isServiceManager ? " dashboard-service-action-grid--five" : ""
              }`}
            >
              <button
                type="button"
                className="action-button dashboard-action-button dashboard-action-button--tile"
                disabled={
                  !hasFeature(FEATURE_CODES.SERVICE_ATTENDANCE_CHECK_IN) ||
                  !selectedServiceSession?.serviceInstanceId
                }
                title={
                  hasFeature(FEATURE_CODES.SERVICE_ATTENDANCE_CHECK_IN)
                    ? undefined
                    : "Check in is not included in your organisation profile"
                }
                onClick={() =>
                  selectedServiceSession && registerSession(selectedServiceSession)
                }
              >
                <HowToRegIcon fontSize="small" />
                <span>Check in</span>
              </button>
              <button
                type="button"
                className="action-button dashboard-action-button dashboard-action-button--tile"
                disabled={!hasFeature(FEATURE_CODES.SERVICE_MEMBERS)}
                title={
                  hasFeature(FEATURE_CODES.SERVICE_MEMBERS)
                    ? undefined
                    : "Members is not included in your organisation profile"
                }
                onClick={() =>
                  history.push(`/services/${selectedService.serviceId}/members`)
                }
              >
                <GroupsIcon fontSize="small" />
                <span>Members</span>
              </button>
              <button
                type="button"
                className="action-button dashboard-action-button dashboard-action-button--tile"
                disabled={!hasFeature(FEATURE_CODES.SERVICE_ROTA)}
                title={
                  hasFeature(FEATURE_CODES.SERVICE_ROTA)
                    ? undefined
                    : "Rota is not included in your organisation profile"
                }
                onClick={() =>
                  history.push(`/services/${selectedService.serviceId}/rota`, {
                    rotaOrigin: "dashboard",
                  })
                }
              >
                <CalendarMonthIcon fontSize="small" />
                <span>Rota</span>
              </button>
              <button
                type="button"
                className="action-button dashboard-action-button dashboard-action-button--tile"
                disabled={!hasFeature(FEATURE_CODES.SERVICE_SESSION_REPORTS)}
                title={hasFeature(FEATURE_CODES.SERVICE_SESSION_REPORTS) ? undefined : "Session reports are not included in your organisation profile"}
                onClick={() =>
                  history.push(
                    `/services/${selectedService.serviceId}/session-reports${selectedServiceSession?.serviceInstanceId ? `?serviceInstanceId=${selectedServiceSession.serviceInstanceId}` : ""}`,
                  )
                }
              >
                <AssessmentIcon fontSize="small" />
                <span>Session reports</span>
              </button>
              {isServiceManager && (
                <button
                  type="button"
                  className="action-button dashboard-action-button dashboard-action-button--tile"
                  disabled={!hasFeature(FEATURE_CODES.SERVICE_REGISTRATION)}
                  title={
                    hasFeature(FEATURE_CODES.SERVICE_REGISTRATION)
                      ? undefined
                      : "New registrations is not included in your organisation profile"
                  }
                  onClick={() =>
                    history.push(
                      `/admin/public-registrations?serviceId=${selectedService.serviceId}`,
                    )
                  }
                >
                  <HowToRegIcon fontSize="small" />
                  <span>New registrations</span>
                  {pendingRegistrationCount !== null && (
                    <strong className="dashboard-action-count">
                      {pendingRegistrationCount}
                    </strong>
                  )}
                </button>
              )}
            </div>
          </div>
          {isTenantAdmin && (
            <DataQualitySection serviceId={selectedService.serviceId} />
          )}
          <div className="dashboard-service-section">
            <h3 className="dashboard-service-section-title">At a glance</h3>
            <div className="dashboard-service-glance" aria-label="Selected service statistics">
              {glanceItems.map((item) => (
                <div className="dashboard-service-glance-item" key={item.label}>
                  <strong>{item.value ?? "—"}</strong>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {loadError && (
        <p className="dashboard-inline-error">
          Upcoming services could not be loaded.
        </p>
      )}
      {!isLoading && !loadError && !rows.length && (
        <p className="muted">No services available for your organisation.</p>
      )}
    </section>
  );
}

function DataQualitySection({ serviceId }: { serviceId: number | null }) {
  const history = useHistory();
  const { isTenantAdmin } = useAuth();
  const [compliance, setCompliance] = useState<DashboardComplianceDto>({
    missingServiceRegistrations: [],
    contactIssues: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState<number | null>(null);
  const contactsRequiringAttention = new Set([
    ...compliance.missingServiceRegistrations.map((item) => item.partyId),
    ...compliance.contactIssues.map((item) => item.partyId),
  ]).size;
  useEffect(() => {
    if (!isTenantAdmin || !serviceId) {
      setCompliance({ missingServiceRegistrations: [], contactIssues: [] });
      return;
    }
    let isMounted = true;

    const loadCompliance = async () => {
      setIsLoading(true);
      setLoadError(false);

      try {
        const data = await api<DashboardComplianceDto>(
          `/api/dashboard/compliance?serviceId=${serviceId}`,
          {
            suppressLoading: true,
            notifyError: false,
          },
        );

        if (isMounted) {
          setCompliance(data);
        }
      } catch (error) {
        console.error("Failed to load dashboard compliance", error);
        if (isMounted) {
          setLoadError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadCompliance();

    return () => {
      isMounted = false;
    };
  }, [isTenantAdmin, serviceId]);

  useEffect(() => {
    if (!isTenantAdmin || !serviceId) {
      setDuplicateCount(null);
      return;
    }
    let cancelled = false;
    api<DuplicatePartyGroup[]>(`/api/duplicate-parties?serviceId=${serviceId}`, {
      suppressLoading: true,
      notifyError: false,
    })
      .then((candidates) => { if (!cancelled) setDuplicateCount(candidates.length); })
      .catch(() => { if (!cancelled) setDuplicateCount(null); });
    return () => { cancelled = true; };
  }, [isTenantAdmin, serviceId]);

  return (
    <div className="dashboard-service-section dashboard-data-quality">
      <h3 className="dashboard-service-section-title">Data quality</h3>

      {isLoading && (
        <div className="dashboard-compliance-loading">
          <CircularProgress size={22} />
        </div>
      )}

      {loadError && (
        <p className="dashboard-inline-error">
          Data quality information could not be loaded.
        </p>
      )}

      {!isLoading && !loadError && (
        <div className="dashboard-compliance-grid">
          <div className="dashboard-compliance-section">
            <button
              type="button"
              className="dashboard-compliance-summary-link"
              disabled={!isTenantAdmin}
              title={
                isTenantAdmin
                  ? undefined
                  : "Tenant administrator access is required to review contact records"
              }
              onClick={() => history.push(`/reports/contacts?serviceId=${serviceId}`)}
            >
              <span className="dashboard-compliance-summary-icon" aria-hidden="true">
                <PersonOutlineIcon />
              </span>
              <span className="dashboard-compliance-summary-content">
                <strong>Missing contact information</strong>
                <small>{contactsRequiringAttention === 0 ? "All member details are complete" : "Service members requiring attention"}</small>
              </span>
              <span className="dashboard-compliance-summary-count">{contactsRequiringAttention}</span>
              <ChevronRightIcon className="dashboard-compliance-summary-chevron" aria-hidden="true" />
            </button>
          </div>

          <div className="dashboard-compliance-section dashboard-compliance-section--duplicates">
            <button
              type="button"
              className="dashboard-compliance-summary-link"
              disabled={!isTenantAdmin}
              title={isTenantAdmin ? undefined : "Tenant administrator access is required to review potential duplicates"}
              onClick={() => history.push(`/admin/duplicate-parties?serviceId=${serviceId}`)}
            >
              <span className="dashboard-compliance-summary-icon" aria-hidden="true">
                <ContentCopyOutlinedIcon />
              </span>
              <span className="dashboard-compliance-summary-content">
                <strong>Potential conflicts</strong>
                <small>{duplicateCount === 0 ? "No member conflicts found" : "Service members requiring review"}</small>
              </span>
              <span className="dashboard-compliance-summary-count">{duplicateCount ?? "–"}</span>
              <ChevronRightIcon className="dashboard-compliance-summary-chevron" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const communityUpdateTypes: CommunityUpdateDto["updateType"][] = [
  "News",
  "Request",
  "Update",
  "Giveaway",
];

function CommunityUpdatesCard() {
  const { isTenantAdmin } = useAuth();
  const [updates, setUpdates] = useState<CommunityUpdateDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showPublisher, setShowPublisher] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [updateType, setUpdateType] =
    useState<CommunityUpdateDto["updateType"]>("Update");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [expiresDate, setExpiresDate] = useState("");

  useEffect(() => {
    let isMounted = true;

    api<CommunityUpdateDto[]>("/api/community-updates?limit=20", {
      suppressLoading: true,
      notifyError: false,
    })
      .then((data) => {
        if (isMounted) setUpdates(data);
      })
      .catch((error) => {
        console.error("Failed to load community updates", error);
        if (isMounted) setLoadError(true);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const publishUpdate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsPublishing(true);

    try {
      const published = await api<CommunityUpdateDto>("/api/community-updates", {
        method: "POST",
        body: {
          updateType,
          title,
          body,
          expiresDate: expiresDate
            ? new Date(`${expiresDate}T23:59:59`).toISOString()
            : null,
        },
        notifySuccess: "Community update published.",
      });
      setUpdates((current) => [published, ...current]);
      setUpdateType("Update");
      setTitle("");
      setBody("");
      setExpiresDate("");
      setShowPublisher(false);
    } finally {
      setIsPublishing(false);
    }
  };

  const formatPublishedDate = (value: string) =>
    new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(value));

  return (
    <section className="card dashboard-panel dashboard-community-updates">
      <div className="dashboard-panel-header dashboard-community-updates-header">
        <div>
          <h2 className="dashboard-panel-title">Community Updates</h2>
          <p className="dashboard-panel-subtitle">
            News, requests, updates and giveaways shared by local organisations.
          </p>
        </div>
        {isTenantAdmin && (
          <button
            type="button"
            className={`action-button ${showPublisher ? "secondary" : ""}`}
            onClick={() => setShowPublisher((current) => !current)}
          >
            {showPublisher ? "Cancel" : "Publish update"}
          </button>
        )}
      </div>

      {showPublisher && (
        <form className="dashboard-community-publisher" onSubmit={publishUpdate}>
          <div className="dashboard-community-publisher-grid">
            <label>
              Type
              <select
                value={updateType}
                onChange={(event) =>
                  setUpdateType(
                    event.target.value as CommunityUpdateDto["updateType"],
                  )
                }
              >
                {communityUpdateTypes.map((type) => (
                  <option value={type} key={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>
            <label className="dashboard-community-publisher-title">
              Title
              <input
                type="text"
                maxLength={200}
                required
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <label>
              Show until (optional)
              <input
                type="date"
                min={toDateInputValue(new Date())}
                value={expiresDate}
                onChange={(event) => setExpiresDate(event.target.value)}
              />
            </label>
          </div>
          <label>
            Details
            <textarea
              rows={4}
              maxLength={2000}
              required
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Share the news, request or items available..."
            />
          </label>
          <div className="dashboard-community-publisher-actions">
            <button
              type="submit"
              className="action-button"
              disabled={isPublishing || !title.trim() || !body.trim()}
            >
              {isPublishing ? "Publishing..." : "Publish"}
            </button>
          </div>
        </form>
      )}

      {isLoading && (
        <div className="dashboard-community-loading">
          <CircularProgress size={24} />
        </div>
      )}
      {loadError && (
        <p className="dashboard-inline-error">
          Community updates could not be loaded.
        </p>
      )}
      {!isLoading && !loadError && updates.length === 0 && (
        <p className="dashboard-community-empty">
          No community updates have been published yet.
        </p>
      )}
      {!isLoading && !loadError && updates.length > 0 && (
        <div className="dashboard-community-feed">
          {updates.map((update) => (
            <article
              className="dashboard-community-update"
              key={update.communityUpdateId}
            >
              <div className="dashboard-community-update-meta">
                <span
                  className={`dashboard-community-type dashboard-community-type--${update.updateType.toLowerCase()}`}
                >
                  {update.updateType}
                </span>
                <strong>{update.organisationName}</strong>
                <span>{formatPublishedDate(update.publishedDate)}</span>
              </div>
              <h3>{update.title}</h3>
              <p>{update.body}</p>
              {update.expiresDate && (
                <span className="dashboard-community-expiry">
                  Available until {formatPublishedDate(update.expiresDate)}
                </span>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default function DashboardPage() {
  const history = useHistory();
  const { hasFeature } = useAccess();
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const quickActions = [
    {
      label: "Services",
      path: "/admin/manage-services",
      icon: <EventAvailableIcon fontSize="small" />,
      available: hasFeature(FEATURE_CODES.SERVICE_DETAILS),
    },
    {
      label: "People",
      path: "/manage-party",
      icon: <PersonOutlineIcon fontSize="small" />,
      available: true,
    },
    {
      label: "Audiences",
      path: "/manage-audiences",
      icon: <GroupsIcon fontSize="small" />,
      available: hasFeature(FEATURE_CODES.MESSAGING_AUDIENCES_COMMS),
    },
    {
      label: "Campaigns",
      path: "/manage-campaigns",
      icon: <SendIcon fontSize="small" />,
      available: hasFeature(FEATURE_CODES.MESSAGING_AUDIENCES_COMMS),
    },
  ];

  return (
    <div className="page-container dashboard-page">
      <PageHeader
        title="FrontLine Community Dashboard"
        subtitle="Organisation overview"
      />

      <section className="dashboard-actions-section">
        <div className="dashboard-quick-actions">
          {quickActions.map((action) => (
            <button
              className="action-button dashboard-action-button dashboard-action-button--nav"
              type="button"
              key={action.label}
              disabled={!action.available}
              title={
                action.available
                  ? undefined
                  : `${action.label} is not included in your organisation profile`
              }
              onClick={() => history.push(action.path)}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="dashboard-grid">
        <ServiceManagementCard
          selectedServiceId={selectedServiceId}
          onSelectedServiceChange={setSelectedServiceId}
        />
      </section>

      <section className="dashboard-grid">
        <CommunityUpdatesCard />
      </section>
    </div>
  );
}
